import { PrinterModel, PrinterMedia } from "sharedTypes/badge"
import { ExportFormat, AVERY_FORMAT_CATEGORIES, formatCategoryTitle } from "./sheetPresets"

export const getAvailablePrinterModels = (): PrinterModel[] => Object.values(PrinterModel)

export const getAvailablePrinterMedia = (): PrinterMedia[] => Object.values(PrinterMedia)

export type MediaColorMode = "black-only" | "black-red" | "full-color"

export type FixedMediaSize = {
  type: "fixed"
  widthMm: number
  heightMm: number
}

export type RollMediaSize = {
  type: "roll"
  widthMm: number
  minLengthMm: number
  maxLengthMm: number
}

export type UnboundedMediaSize = {
  type: "unbounded"
  minWidthMm?: number
  maxWidthMm?: number
  minHeightMm?: number
  maxHeightMm?: number
}

export type MediaSize = FixedMediaSize | RollMediaSize | UnboundedMediaSize

export type MediaDescriptor = {
  id: PrinterMedia
  label: string
  size: MediaSize
  colorMode: MediaColorMode
  supportsImages: boolean
  supportsQR: boolean
  maxResolutionDPI: number
}

export type PrinterDescriptor = {
  id: PrinterModel
  label: string
  supportedMedia: PrinterMedia[]
  supportsVariableLength?: boolean
  maxWidthMm?: number
  maxHeightMm?: number
}

export const MEDIA_DESCRIPTORS: Record<PrinterMedia, MediaDescriptor> = {
  [PrinterMedia.DK_2205]: {
    id: PrinterMedia.DK_2205,
    label: "Brother DK-2205 (Black)",
    size: {
      type: "roll",
      widthMm: 62,
      minLengthMm: 20,
      maxLengthMm: 120,
    },
    colorMode: "black-only",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
  [PrinterMedia.DK_2251]: {
    id: PrinterMedia.DK_2251,
    label: "Brother DK-2251 (Black/Red)",
    size: {
      type: "roll",
      widthMm: 62,
      minLengthMm: 20,
      maxLengthMm: 120,
    },
    colorMode: "black-red",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
  [PrinterMedia.GENERIC]: {
    id: PrinterMedia.GENERIC,
    label: "Generic Paper",
    size: {
      type: "unbounded",
      minWidthMm: 1,
      maxWidthMm: 1000,
      minHeightMm: 1,
      maxHeightMm: 1000,
    },
    colorMode: "full-color",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
  [PrinterMedia.AVERY_5392]: {
    id: PrinterMedia.AVERY_5392,
    label: `${formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392])} (${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392].labelSize})`,
    size: {
      type: "fixed",
      widthMm: 101.6,
      heightMm: 76.2,
    },
    colorMode: "full-color",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
  [PrinterMedia.AVERY_5390]: {
    id: PrinterMedia.AVERY_5390,
    label: `${formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390])} (${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390].labelSize})`,
    size: {
      type: "fixed",
      widthMm: 88.9,
      heightMm: 57.15,
    },
    colorMode: "full-color",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
  [PrinterMedia.AVERY_8395]: {
    id: PrinterMedia.AVERY_8395,
    label: `${formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395])} (${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395].labelSize})`,
    size: {
      type: "fixed",
      widthMm: 85.725,
      heightMm: 59.267,
    },
    colorMode: "full-color",
    supportsImages: true,
    supportsQR: true,
    maxResolutionDPI: 300,
  },
}

export const PRINTER_DESCRIPTORS: Record<PrinterModel, PrinterDescriptor> = {
  [PrinterModel.QL_810_820_SERIES]: {
    id: PrinterModel.QL_810_820_SERIES,
    label: "Brother QL-800 Series",
    supportedMedia: [PrinterMedia.DK_2205, PrinterMedia.DK_2251],
    supportsVariableLength: true,
  },
  [PrinterModel.GENERIC]: {
    id: PrinterModel.GENERIC,
    label: "Generic Printer (PDF Export)",
    supportedMedia: [
      PrinterMedia.GENERIC, PrinterMedia.AVERY_5392,
      PrinterMedia.AVERY_5390, PrinterMedia.AVERY_8395,
    ],
    supportsVariableLength: true,
  },
}

export const PrinterModelLabels: Record<PrinterModel, string> = Object.values(PRINTER_DESCRIPTORS)
  .reduce((acc, descriptor) => {
    acc[descriptor.id] = descriptor.label

    return acc
  }, {} as Record<PrinterModel, string>)

export const PrinterMediaLabels: Record<PrinterMedia, string> = Object.values(MEDIA_DESCRIPTORS)
  .reduce((acc, descriptor) => {
    acc[descriptor.id] = descriptor.label

    return acc
  }, {} as Record<PrinterMedia, string>)

export const getPrinterDescriptor = (printerModel: PrinterModel): PrinterDescriptor => {
  const descriptor = PRINTER_DESCRIPTORS[printerModel]

  if (!descriptor) {
    throw new Error(`Unknown printer model: ${printerModel}`)
  }

  return descriptor
}

export const getMediaDescriptor = (printerMedia: PrinterMedia): MediaDescriptor => {
  const descriptor = MEDIA_DESCRIPTORS[printerMedia]

  if (!descriptor) {
    throw new Error(`Unknown printer media: ${printerMedia}`)
  }

  return descriptor
}

export const getCompatibleMediaForPrinter = (
  printerModel: PrinterModel,
): PrinterMedia[] => getPrinterDescriptor(printerModel).supportedMedia

export const isCompatibleCombination = (
  printerModel: PrinterModel,
  printerMedia: PrinterMedia,
): boolean => getCompatibleMediaForPrinter(printerModel).includes(printerMedia)
